/**
 * Hand-replay recorder — PROTOTYPE.
 *
 * Purpose of this prototype: capture the raw table-data frames the client
 * receives (GOT_TEXAS / GOT_OMAHA) so we can (a) measure how big a recording
 * actually gets and whether we'll need to trim, and (b) prove we can replay the
 * whole thing back through the real dispatch → render path.
 *
 * It is intentionally minimal (console-driven, no file format versioning beyond a
 * marker). If the size numbers look workable we'll build the real design in
 * docs/development/game-client-hand-replay-plan.md.
 *
 * Recording is a cheap no-op until started (a bool check + push), mirroring
 * perfCounters — safe to leave in the frame path.
 */
import { GOT_OMAHA, GOT_TEXAS } from 'constants/eventConstants';
import pako from 'pako';
import { updateCurrentPlayerGUID, updateCurrentTable } from 'reducers/currentTable';
import { removeTable } from 'reducers/tables';
import store from 'store';

import { decompressData } from './functions';

type RawFrame = { t: number; event: string; data: unknown };

let recording = false;
let replaying = false;
let replayPaused = false;
let replayAborted = false;
let replaySpeed = 1;
let replayIndex = 0;
let replayTotal = 0;
let startedAt = 0;
let frames: RawFrame[] = [];
let povGuid = '';

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

// Multi-table replay opens tables one at a time (like a real player) rather than
// all at once, so each open's mount/render spike shows up. This is the gap (in
// recorded-time ms) between successive table opens; at replay speed S the real
// gap is STAGGER_MS / S.
const STAGGER_MS = 2500;

// Sentinel event for a recorded view/mode switch (URL change) — replayed by
// navigating the same way, so the cost of switching table views is captured.
const NAV_EVENT = '__nav__';

/** Set replay speed (× real time). Takes effect immediately, even mid-replay. */
export const setReplaySpeed = (s: number): void => {
  replaySpeed = s > 0 ? s : 1;
};
export const getReplaySpeed = (): number => replaySpeed;
/** Progress of the running replay: frames applied and total. */
export const getReplayProgress = (): { index: number; total: number } => ({
  index: replayIndex,
  total: replayTotal
});

/** True while a replay is driving the store — the live socket handlers check
 *  this and drop incoming frames so live updates don't fight the replay. */
export const isReplaying = (): boolean => replaying;
/** True while a running replay is paused. */
export const isReplayPaused = (): boolean => replayPaused;
/** Manually gate replay mode (PerfHud sets it before navigating to the replay
 *  table so subscribe-on-mount frames are dropped from the very first tick). */
export const setReplaying = (value: boolean): void => {
  replaying = value;
};
/** Pause / resume / stop a running replay (from the HUD buttons). */
export const pauseReplay = (): void => {
  replayPaused = true;
};
export const resumeReplay = (): void => {
  replayPaused = false;
};
export const stopReplay = (): void => {
  replayAborted = true;
  replayPaused = false;
};

/**
 * Load a previously-exported recording (parsed JSON) into the recorder so it can
 * be replayed/exported. Replaces any current capture. Returns false if the shape
 * isn't a recording. (In-memory captures don't survive a reload — import the
 * exported .json to bring one back.)
 */
export const loadRecording = (rec: any): boolean => {
  if (!rec || !Array.isArray(rec.frames)) return false;
  frames = rec.frames.map((f: any) => ({
    t: Number(f?.t) || 0,
    event: String(f?.event ?? ''),
    data: f?.data
  }));
  povGuid = String(rec?.pov?.playerGUID ?? '');
  recording = false;
  return true;
};

/**
 * Distinct tables in the capture (id + type), so replay can open the right view:
 * one table → single-table view; several → the multi-table grid (max-load case).
 * Scans frames once and decompresses to read each `game.gameID`.
 */
export const getCaptureTables = (): { gameID: string; gameType: string }[] => {
  const byId = new Map<string, string>(); // gameID -> gameType
  for (const f of frames) {
    if (f.event !== GOT_TEXAS && f.event !== GOT_OMAHA) continue;
    try {
      const p: any = decompressData(f.data);
      const gid = String(p?.game?.gameID ?? '');
      if (!gid || gid === '0' || byId.has(gid)) continue;
      byId.set(gid, String(p?.game?.game || (f.event === GOT_OMAHA ? 'omaha' : 'texas')));
    } catch {
      /* skip a bad frame */
    }
  }
  return Array.from(byId, ([gameID, gameType]) => ({ gameID, gameType }));
};

/** The single busiest/first captured table (for single-table replay). */
export const getCaptureTarget = (): { gameID: string; gameType: string } | null =>
  getCaptureTables()[0] ?? null;

/**
 * Seed only the FIRST captured table so the grid opens with one cell; the rest
 * are added one at a time by the staggered replay (see STAGGER_MS). Use this for
 * multi-table replay so the open ramp (and its spikes) is exercised.
 */
export const primeFirstReplayTable = (): void => {
  for (const f of frames) {
    if (f.event !== GOT_TEXAS && f.event !== GOT_OMAHA) continue;
    try {
      const p: any = decompressData(f.data);
      const gid = String(p?.game?.gameID ?? '');
      if (!gid || gid === '0') continue;
      store.dispatch({ ...updateCurrentTable(p), meta: { gameID: gid } });
      return; // first table only
    } catch {
      /* skip */
    }
  }
};

/** Remove the replayed tables from state.tables so nothing lingers afterward. */
export const clearReplayedTables = (): void => {
  for (const { gameID } of getCaptureTables()) {
    try {
      store.dispatch(removeTable(gameID));
    } catch {
      /* ignore */
    }
  }
};

/** UTF-8 byte length of a string, or of the JSON of a value. */
const byteLen = (value: unknown): number => {
  const s = typeof value === 'string' ? value : JSON.stringify(value ?? null);
  return new TextEncoder().encode(s).length;
};

const fmtBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const isRecording = (): boolean => recording;

/** Number of frames captured so far — for the HUD's live "rec Nf" readout. */
export const getFrameCount = (): number => frames.length;

/** Whether there's a finished capture available to export/replay. */
export const hasCapture = (): boolean => !recording && frames.length > 0;

/**
 * Free the in-memory capture (the frames array). A recording is a deep clone of
 * every decompressed table payload, so a multi-table capture can hold ~10MB+
 * resident indefinitely — it's kept after stop() so it can still be exported and
 * replayed, but once you're done it's dead weight. Call this to reclaim it.
 * No-op while actively recording (use stop() first).
 */
export const clearCapture = (): void => {
  if (recording) return;
  frames = [];
  povGuid = '';
  replayIndex = 0;
  replayTotal = 0;
};

/** Begin capturing table frames. Clears any prior capture. */
export const startRecording = (): void => {
  frames = [];
  startedAt = performance.now();
  try {
    povGuid = window.localStorage.getItem('playerGUID') || '';
  } catch {
    povGuid = '';
  }
  recording = true;
  // eslint-disable-next-line no-console
  console.log('[handRec] recording… (play a hand, then handRec.stop())');
};

/**
 * Record one inbound table frame. Called from the socket handler in App.tsx.
 * Strings (compressed base64) are stored as-is; objects are cloned so a later
 * in-place mutation can't corrupt the capture.
 */
export const recordFrame = (event: string, data: unknown): void => {
  if (!recording) return;
  let stored: unknown = data;
  if (data && typeof data === 'object') {
    try {
      stored = JSON.parse(JSON.stringify(data));
    } catch {
      stored = data;
    }
  }
  frames.push({ t: performance.now() - startedAt, event, data: stored });
};

/**
 * Record a view/mode switch (URL change) while recording, so replay can
 * reproduce the same navigation and capture the cost of switching table views.
 * Called from BenchmarkTableBridge on every route change.
 */
export const recordNav = (path: string): void => {
  if (!recording) return;
  // Skip a no-op repeat of the current view.
  const lastNav = [...frames].reverse().find((f) => f.event === NAV_EVENT);
  if (lastNav && lastNav.data === path) return;
  frames.push({ t: performance.now() - startedAt, event: NAV_EVENT, data: path });
};

/** Build the exportable recording object. */
const buildRecording = () => ({
  proto: 'hand-replay-0',
  recordedAt: new Date().toISOString(),
  pov: { playerGUID: povGuid },
  frameCount: frames.length,
  frames: frames.map((f) => ({ t: Math.round(f.t), event: f.event, data: f.data }))
});

/**
 * Print a size + shape report for the current capture — the whole point of the
 * prototype. Shows raw wire bytes vs. decompressed bytes, the exported file size
 * (and gzipped), per-event breakdown, rate, and a per-hour projection so we can
 * judge whether trimming is needed.
 */
export const recordingStats = () => {
  const n = frames.length;
  if (n === 0) {
    // eslint-disable-next-line no-console
    console.log('[handRec] no frames captured yet.');
    return null;
  }
  const durationSec = frames[n - 1].t / 1000;
  let rawBytes = 0;
  let decompressedBytes = 0;
  const byEvent: Record<string, { count: number; rawBytes: number }> = {};
  const tableSet = new Set<string>();
  for (const f of frames) {
    const raw = byteLen(f.data);
    rawBytes += raw;
    try {
      const d: any = decompressData(f.data);
      decompressedBytes += byteLen(d);
      const gid = String(d?.game?.gameID ?? '');
      if (gid && gid !== '0') tableSet.add(gid);
    } catch {
      /* ignore a bad frame in the size tally */
    }
    const e = (byEvent[f.event] ??= { count: 0, rawBytes: 0 });
    e.count += 1;
    e.rawBytes += raw;
  }

  const exportJson = JSON.stringify(buildRecording());
  const exportBytes = byteLen(exportJson);
  let gzippedBytes = 0;
  try {
    gzippedBytes = pako.gzip(exportJson).length;
  } catch {
    /* pako failure — leave 0 */
  }

  const framesPerSec = durationSec > 0 ? n / durationSec : 0;
  const bytesPerSec = durationSec > 0 ? exportBytes / durationSec : 0;

  const stats = {
    frames: n,
    tables: tableSet.size,
    durationSec: Math.round(durationSec * 10) / 10,
    framesPerSec: Math.round(framesPerSec * 10) / 10,
    rawWire: fmtBytes(rawBytes),
    decompressed: fmtBytes(decompressedBytes),
    exportFile: fmtBytes(exportBytes),
    exportGzipped: fmtBytes(gzippedBytes),
    avgFrame: fmtBytes(rawBytes / n),
    projectedPerHour: fmtBytes(bytesPerSec * 3600),
    projectedPerHourGzip:
      exportBytes > 0 ? fmtBytes((gzippedBytes / exportBytes) * bytesPerSec * 3600) : '—'
  };

  /* eslint-disable no-console */
  console.log('[handRec] recording stats:');
  console.table(stats);
  console.log(
    '[handRec] per-event:',
    Object.fromEntries(
      Object.entries(byEvent).map(([ev, v]) => [ev, { count: v.count, size: fmtBytes(v.rawBytes) }])
    )
  );
  /* eslint-enable no-console */
  return stats;
};

/** Stop capturing and print the size report. */
export const stopRecording = () => {
  recording = false;
  // eslint-disable-next-line no-console
  console.log(`[handRec] stopped: ${frames.length} frames`);
  return recordingStats();
};

/** Download the current capture as a JSON file to share with other devs. */
export const exportRecording = (): void => {
  const json = JSON.stringify(buildRecording());
  try {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hand-replay-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    a.rel = 'noopener';
    a.style.display = 'none';
    // MOBILE EXPORT. Two things that work on desktop but silently do nothing on
    // Chrome for Android, which is where QA actually captures:
    //   1. A DETACHED anchor's click() is ignored — the element must be in the
    //      document for the download to be dispatched.
    //   2. The download is ASYNCHRONOUS, so revoking the blob URL on the next
    //      line tears the data down before the browser has read it. A multi-MB
    //      capture never lands.
    // So: attach, click, and revoke on a later task once the write is underway.
    document.body.appendChild(a);
    a.click();
    window.setTimeout(() => {
      if (a.parentNode) a.parentNode.removeChild(a);
      URL.revokeObjectURL(url);
    }, 30000);
    // eslint-disable-next-line no-console
    console.log(`[handRec] exported ${fmtBytes(byteLen(json))}`);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[handRec] export failed', e);
  }
};

/**
 * Replay the current capture (or a loaded one) back through the real reducers, at
 * recorded cadence (÷ speed). Proves the captured data is sufficient to reproduce
 * the hand. Be on the recorded table's view to actually watch it render.
 */
export const replayRecording = async (
  recording_?: ReturnType<typeof buildRecording>,
  speed = 1,
  onNav?: (path: string) => void
): Promise<void> => {
  const rec = recording_ ?? buildRecording();
  const f = rec.frames;
  if (!f.length) {
    // eslint-disable-next-line no-console
    console.log('[handRec] nothing to replay.');
    return;
  }
  // One captured table → drive currentTable (single view). Several → drive the
  // tables slice per gameID so the multi-table grid renders them, opening one at
  // a time (staggered) rather than all at once.
  const multi = getCaptureTables().length > 1;
  // Whether the capture includes view/mode switches to reproduce.
  const hasNav = f.some((fr) => fr.event === NAV_EVENT);
  if (rec.pov?.playerGUID) store.dispatch(updateCurrentPlayerGUID(rec.pov.playerGUID));
  // eslint-disable-next-line no-console
  console.log(
    `[handRec] replaying ${f.length} frames at ${speed}×${multi ? ' (multi-table)' : ''}…`
  );
  // Gate live socket frames for the whole replay so they can't clash with the
  // replayed state (App.tsx checks isReplaying()).
  replaying = true;
  replayPaused = false;
  replayAborted = false;
  replaySpeed = speed > 0 ? speed : 1;
  replayTotal = f.length;
  replayIndex = 0;
  // Multi-table: stagger when each table "opens" so they ramp in one at a time
  // (like a player), instead of all appearing at once. A table's frames are held
  // until its open time (nth distinct table → n × STAGGER_MS in recorded time).
  const openAtMs: Record<string, number> = {};
  // The table currently on screen, derived from replayed NAV frames. Mirrors the
  // production dispatch path (App.tsx): only the focused table updates the shared
  // currentTable slice, so the App root re-renders once per frame instead of once
  // per open table. Empty in grid/lobby (no single focused table), same as prod.
  let focusedGid = '';
  try {
    let prev = 0;
    for (let i = 0; i < f.length; i++) {
      if (replayAborted) break;
      const fr = f[i];
      // Read replaySpeed each frame so a mid-replay speed change takes effect.
      const wait = (fr.t - prev) / (replaySpeed || 1);
      prev = fr.t;
      if (wait > 0) await sleep(wait);
      // Hold here while paused (still gating live frames), bail if stopped.
      while (replayPaused && !replayAborted) await sleep(100);
      if (replayAborted) break;
      // A recorded view/mode switch — reproduce the navigation (the caller maps
      // the path to the real view transition) so its cost is measured.
      if (fr.event === NAV_EVENT) {
        const navPath = String(fr.data);
        // Track the focused table so currentTable writes match production. A
        // /table/:gid route is a single focused table; /grid and lobby have none.
        const navMatch = navPath.match(/^\/table\/([^/?]+)/);
        focusedGid = navMatch ? navMatch[1] : '';
        onNav?.(navPath);
        replayIndex = i + 1;
        continue;
      }
      const payload: any = decompressData(fr.data);
      if (multi) {
        // Route to the frame's own table in the tables slice → grid cell.
        const gid = String(payload?.game?.gameID ?? '');
        if (!gid || gid === '0') {
          replayIndex = i + 1;
          continue;
        }
        // Assign each table an open time by discovery order; skip its frames
        // until then so the grid grows one cell at a time.
        if (!(gid in openAtMs)) openAtMs[gid] = Object.keys(openAtMs).length * STAGGER_MS;
        if (fr.t < openAtMs[gid]) {
          replayIndex = i + 1;
          continue;
        }
        store.dispatch({ ...updateCurrentTable(payload), meta: { gameID: gid } });
        // Only the on-screen (focused) table drives the shared currentTable slice,
        // matching production (App.tsx). The focused single-table view still reads
        // its own tables[gid] entry above; this write is just for currentTable's
        // legacy consumers (join/blind/support/maintenance).
        if (hasNav && gid === focusedGid) store.dispatch(updateCurrentTable(payload));
      } else {
        // Single-table replay renders from currentTable only. No meta.gameID —
        // that would leave a lingering "open" table the user could wander into.
        store.dispatch(updateCurrentTable(payload));
      }
      replayIndex = i + 1;
    }
  } finally {
    replaying = false;
    replayPaused = false;
  }
  // eslint-disable-next-line no-console
  console.log(replayAborted ? '[handRec] replay stopped.' : '[handRec] replay done.');
};
